import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Button from "react-bootstrap/Button";
// react-pdf 공식 스타일 추가
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import Swal from '../../Components/SweetAlert2';

// PDF.js 워커 경로를 public 폴더로 지정
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

function PDFViewerWithSumit({ fileUrl, selectedData, Sumit, initialPage = 1, fitHeight, dataList, setSelectedData, navigate, onAfterComplete, onPageChangeNav, containerWidth }) {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [sentLastPage, setSentLastPage] = useState(false);
  const containerRef = useRef(null);
  const pdfDocRef = useRef();
  const buttonAreaRef = useRef();
  const [pageWidth, setPageWidth] = useState(800);
  // sumit 호출 여부를 제어하는 상태 추가
  const [sentCompleted, setSentCompleted] = useState(false);
  const [containerMinHeight, setContainerMinHeight] = useState(0);
  const [pageDims, setPageDims] = useState({ width: 800, height: 1131, aspect: 800 / 1131 });
  const [containerDims, setContainerDims] = useState({ width: 800, height: 600 });

  // 모바일 환경 감지
  const isMobile = window.innerWidth <= 768;

  // PDF 로드 완료 시 전체 페이지 수 저장
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // PDF 원본 비율 저장
  const onPageLoadSuccess = ({ width, height }) => {
    setPageDims({ width, height, aspect: width / height });
  };

  // 컨테이너 크기 측정
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerDims({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 컨테이너의 높이를 vh 단위로 지정
  const containerHeight = Math.max(window.innerHeight * 0.8, 400); // 최소 400px, 최대 80vh

  // PDF가 짤리지 않게 비율에 맞는 width/height 계산
  let targetWidth = containerDims.width;
  let targetHeight = containerHeight;
  if (targetWidth / pageDims.aspect > targetHeight) {
    targetWidth = targetHeight * pageDims.aspect;
  } else {
    targetHeight = targetWidth / pageDims.aspect;
  }

  // sumit useEffect - page > 1일 때마다 sumit 호출, 서버 응답 콘솔 출력, 진행률 즉시 갱신
  React.useEffect(() => {
    if (!numPages || !page || !selectedData) return;
    if (selectedData.viewedValue === selectedData.totalValue) return;
    if (page === numPages) {
      if (!sentCompleted && page > 1) {
        Sumit(numPages, selectedData.id, selectedData.eduId).then((res) => {
          console.log('[PDF] 마지막 페이지 sumit 응답:', res);
        });
        setSentCompleted(true);
        if (typeof onAfterComplete === 'function') onAfterComplete();
        console.log(`[PDF] 마지막 페이지 sumit(1회만): page=${numPages}, id=${selectedData.id}, eduId=${selectedData.eduId}`);
      }
      return;
    }
    // 마지막 페이지가 아니면 sumit 정상 호출 (1페이지 제외)
    if (page > 1 && page > selectedData.viewedValue) {
      Sumit(page, selectedData.id, selectedData.eduId).then((res) => {
        console.log('[PDF] 페이지 이동 sumit 응답:', res);
      });
      if (typeof onAfterComplete === 'function') onAfterComplete();
    }
    console.log(`[PDF] 페이지 이동 sumit: page=${page}, id=${selectedData.id}, eduId=${selectedData.eduId}`);
  }, [page, numPages]);

  // initialPage prop이 바뀌면 페이지 이동 (같은 값이면 setPage 호출하지 않음)
  React.useEffect(() => {
    if (initialPage !== page) {
      setPage(initialPage);
    }
  }, [initialPage]);

  // 컨테이너 크기에 맞춰 PDF width 자동 조정 (모바일/데스크탑 분기)
  React.useEffect(() => {
    if (!fitHeight) return;
    const handleResize = () => {
      if (containerRef.current) {
        if (window.innerWidth <= 768) {
          setPageWidth(window.innerWidth - 32); // 모바일: 가로에만 fit
        } else {
          setPageWidth(containerRef.current.offsetWidth - 32); // 데스크탑: padding 고려
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fitHeight]);

  // PDF 뷰어 모바일 스타일 개선: 화면에 꽉 차게, 오버플로우 방지 + 불필요한 공백 제거
  React.useEffect(() => {
    if (!containerRef.current) return;
    if (isMobile) {
      containerRef.current.style.width = '100%';
      containerRef.current.style.maxWidth = '100%';
      containerRef.current.style.minHeight = 'auto';
      containerRef.current.style.height = 'auto';
      containerRef.current.style.overflowX=  'scroll';
      containerRef.current.style.padding = '0';
      containerRef.current.style.margin = '0';
    } else {
      containerRef.current.style.width = '100%';
      containerRef.current.style.maxWidth = '100%';
      containerRef.current.style.minHeight = 'auto';
      containerRef.current.style.height = 'auto';
      containerRef.current.style.overflowX = 'hidden';
      containerRef.current.style.padding = '0';
      containerRef.current.style.margin = '0';
    }
  }, [isMobile]);

  // PDF/버튼 실제 높이 측정 후 합산
  useEffect(() => {
    const pdfHeight = pdfDocRef.current?.offsetHeight || 0;
    const btnHeight = buttonAreaRef.current?.offsetHeight || 0;
    setContainerMinHeight(pdfHeight + btnHeight);
  }, [page, numPages, isMobile, containerWidth]);

  // 네비게이션 버튼 내부 렌더링
  const handlePageChange = (direction) => {
    // 수강 완료 자료는 버튼 비활성화 없이 즉시 탐색 가능
    const isCompleted = selectedData && selectedData.viewedValue === selectedData.totalValue;
    if (!isCompleted && isBtnDisabled) return;
    if (!isCompleted) {
      setIsBtnDisabled(true);
      setTimeout(() => setIsBtnDisabled(false), 1000); // 버튼 비활성화 시간
    }
    setPage((prev) => {
      let next = direction === "next" ? prev + 1 : prev - 1;
      if (next < 1) next = 1;
      if (next > numPages) next = numPages;
      return next;
    });
  };

  // 마지막 페이지 sumit도 동일하게 분기 (불필요한 마지막 페이지 sumit useEffect 제거)
  // (불필요한 마지막 페이지 sumit useEffect 제거)

  // 페이지가 바뀌거나 selectedData가 바뀔 때 sumit 완료 상태 초기화
  React.useEffect(() => {
    if (page !== numPages && sentCompleted) {
      setSentCompleted(false);
    }
  }, [page, numPages, sentCompleted]);

  // 페이지가 바뀔 때마다 onPageChangeNav 콜백 호출
  React.useEffect(() => {
    if (typeof onPageChangeNav === 'function') {
      onPageChangeNav(page);
    }
  }, [page, onPageChangeNav]);

  // 미리 렌더링할 페이지 번호 계산
  const prevPage = page > 1 ? page - 1 : null;
  const nextPage = numPages && page < numPages ? page + 1 : null;

  const navStyle = isMobile
    ? { marginTop: 12, marginBottom: 0, justifyContent: 'center', width: window.innerWidth - 32, maxWidth: window.innerWidth - 32, display: 'flex' }
    : { marginTop: 24, justifyContent: 'flex-end', maxWidth: '100vw', display: 'flex', marginRight: '40px'};

  // CSS를 위한 스타일 추가
  const pdfContainerStyle = isMobile ? {
    position: 'relative',
    minHeight: '30vh',
    width: window.innerWidth - 32,
  } : {
    position: 'relative',
    minHeight: '60vh',
  };

  // pageStyle에서 position: absolute 등 겹침 관련 스타일 제거
  const pageStyle = (isVisible) => ({
    position: isVisible ? 'static' : 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    opacity: isVisible ? 1 : 0,
    pointerEvents: isVisible ? 'all' : 'none',
  });

  const pdfContentWidth = isMobile ? window.innerWidth - 32 : containerWidth;

  return (
    <div
      ref={containerRef}
      style={{
        width: isMobile ? window.innerWidth - 32 : '100%',
        maxWidth: isMobile ? window.innerWidth - 32 : '100%',
        height: containerHeight,
        overflowX: 'hidden',
        display: 'block',
        background: '#fff',
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
      }}
    >
      {/* PDF 영역 */}
      <div>
        <Document
          file={fileUrl}
          key={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div></div>}
        >
          {/* 현재, 이전, 다음 페이지를 모두 렌더링 */}
          {[page - 1, page, page + 1].map((p) => {
            if (p < 1 || p > numPages) return null;
            return (
              <div key={`page_wrapper_${p}`} style={pageStyle(p === page)}>
                <Page
                  pageNumber={p}
                  width={targetWidth}
                  height={targetHeight}
                  loading={<div></div>}
                  onLoadSuccess={onPageLoadSuccess}
                />
              </div>
            );
          })}
        </Document>
      </div>
      {/* 버튼 영역 */}
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <div className='gap-2' style={navStyle}>
          <Button
            onClick={() => handlePageChange("prev")}
            disabled={(!selectedData || selectedData.viewedValue !== selectedData.totalValue) ? (isBtnDisabled || page === 1) : (page === 1)}
            style={{ marginRight: 8 }}
          >
            ◀ 이전
          </Button>
          <span style={{ fontSize: '1rem', alignSelf: 'center' }}>
            {page} / {numPages}
          </span>
          <Button
            onClick={() => handlePageChange("next")}
            disabled={(!selectedData || selectedData.viewedValue !== selectedData.totalValue) ? (isBtnDisabled || page === numPages) : (page === numPages)}
            style={{ marginLeft: 8 }}
          >
            다음 ▶
          </Button>
          {/* 마지막 페이지에서 수강 완료 안내를 버튼으로 */}
          {page === numPages && numPages && selectedData?.viewedValue !== selectedData?.totalValue && (
            <button
              style={{
                color: '#fff',
                background: '#198754',
                fontWeight: 700,
                marginLeft: 12,
                alignSelf: 'center',
                fontSize: '1rem',
                border: 'none',
                borderRadius: 8,
                padding: '4px 16px',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: '0 2px 8px rgba(25,135,84,0.08)'
              }}
              onClick={() => {
                if (Array.isArray(dataList)) {
                  const remain = dataList.filter(item => item.viewedValue !== item.totalValue);
                  if (
                    remain.length === 1 &&
                    ((remain[0].id === selectedData.id && remain[0].eduId === selectedData.eduId) || (remain[0].eduId === selectedData.eduId))
                  ) {
                    // 마지막 컨텐츠 - 알림창
                    if (typeof Swal !== 'undefined' && Swal.custom) {
                      Swal.custom({
                        title: '모든 교육을 이수하였습니다',
                        icon: 'success',
                        confirmButtonText: '확인',
                      }).then((result) => {
                        if (result.isConfirmed && typeof navigate === 'function') {
                          navigate(-1);
                        }
                      });
                    } else if (typeof window !== 'undefined') {
                      alert('모든 교육을 이수하였습니다.');
                      if (typeof navigate === 'function') navigate(-1);
                    }
                    return;
                  }
                  if (remain.length > 0 && remain[0].eduId === selectedData.eduId) {
                    setSelectedData(remain[1]);
                    if (typeof onAfterComplete === 'function') onAfterComplete();
                  } else {
                    // 남은 교육 없음, 무조건 알림창
                    if (typeof Swal !== 'undefined' && Swal.custom) {
                      Swal.custom({
                        title: '모든 교육을 이수하였습니다',
                        icon: 'success',
                        confirmButtonText: '확인',
                      }).then((result) => {
                        if (result.isConfirmed && typeof navigate === 'function') {
                          navigate(-1);
                        }
                      });
                    } else if (typeof window !== 'undefined') {
                      alert('모든 교육을 이수하였습니다.');
                      if (typeof navigate === 'function') navigate(-1);
                    }
                    return;
                  }
                }
              }}
            >
              교육 완료!
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PDFViewerWithSumit; 